import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { HeroPreviewCard } from "./HeroPreviewCard";

/**
 * HeroSection — primera fold de la landing.
 * Trust badge, headline, descripción, CTAs + preview card del Diagnóstico.
 */
export function HeroSection({ user, primaryCtaHref }) {
  return (
    <section className="relative overflow-hidden hero-warm-gradient border-b border-gray-200">
      {/* Pattern decorativo */}
      <div className="absolute inset-0 dot-pattern opacity-50 pointer-events-none" aria-hidden="true" />

      <div className="relative container-prose grid lg:grid-cols-12 gap-10 py-20 lg:py-28 items-center">
        <div className="lg:col-span-7">
          {/* Trust badge superior */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-brand-burgundy/15 rounded-full text-xs text-brand-burgundy mb-6 shadow-sm">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-burgundy" aria-hidden="true" />
            <span className="font-medium">Revisión humana experta · 35 normas en lista blanca</span>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] text-gray-900 mb-6">
            Diagnóstico precontractual con{" "}
            <span className="text-brand-burgundy">IA y revisión humana</span>{" "}
            para compras públicas en Colombia.
          </h1>

          <p className="text-base sm:text-lg text-gray-700 max-w-2xl mb-8 leading-relaxed">
            Procure ayuda a entidades, equipos jurídicos y proveedores a anticipar riesgos antes
            de iniciar un proceso de contratación pública. <strong>Insumo de apoyo</strong> basado
            en lista blanca normativa y revisión humana experta — no sustituye la decisión final
            de la entidad.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Button
              asChild
              size="lg"
              className="rounded-sm h-12 px-7 text-sm tracking-wide bg-brand-burgundy hover:bg-brand-burgundy-light text-white shadow-md hover:shadow-lg transition-all"
              data-testid="hero-cta-prevalidar"
            >
              <Link to={primaryCtaHref}>
                Prevalidar mi caso gratis
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
            {!user && (
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-sm h-12 px-7 text-sm tracking-wide border-gray-300 hover:border-brand-burgundy hover:text-brand-burgundy"
                data-testid="hero-cta-login"
              >
                <Link to="/login">Ya tengo cuenta</Link>
              </Button>
            )}
            <a
              href="#niveles"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-600 hover:text-brand-burgundy transition-colors px-2"
            >
              Ver niveles <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          {/* Trust microcopy bullets */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-600 mb-6">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-brand-coral" aria-hidden="true" />
              Sin tarjeta para prevalidar
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-brand-coral" aria-hidden="true" />
              Trazabilidad legal completa
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-brand-coral" aria-hidden="true" />
              Colombia · Ley 1581/2012
            </span>
          </div>

          <p className="text-xs text-gray-500 max-w-lg" data-testid="hero-mini-disclaimer">
            Insumo de apoyo. No sustituye asesoría legal formal ni la decisión del ordenador
            del gasto o del comité asesor.
          </p>
        </div>

        {/* Mockup visual derecha — tarjeta "snippet" Diagnóstico */}
        <div className="lg:col-span-5">
          <HeroPreviewCard />
          <p className="text-xs text-gray-500 mt-3 leading-relaxed text-center">
            Las entregas <strong>Revisadas</strong> siempre pasan por revisión humana experta
            antes de la descarga.
          </p>
        </div>
      </div>
    </section>
  );
}
